import { useMemo, useState } from "react";
import { limit, orderBy, where } from "firebase/firestore";
import { Search, X } from "lucide-react";
import { formatClinicDate, formatClinicTime, type Appointment, type Patient } from "@cop/domain";
import { useLive } from "../lib/data";
import { StatusPill } from "../components/StatusPill";

function PatientDetail({ patient, onClose }: { patient: Patient; onClose: () => void }) {
  const { data: appts } = useLive<Appointment>("appointments", [where("patientId", "==", patient.id)], [patient.id]);
  const sorted = [...(appts ?? [])].sort((a, b) => b.start.localeCompare(a.start));
  return (
    <aside className="panel" aria-labelledby="patient-title">
      <header className="panel-head">
        <h2 id="patient-title">
          {patient.firstName} {patient.lastName}
        </h2>
        <button className="icon-btn" onClick={onClose} aria-label="Cerrar">
          <X size={18} aria-hidden />
        </button>
      </header>
      <div className="panel-body">
        <dl className="kv">
          <dt>Teléfono</dt>
          <dd>{patient.phone ?? "—"}</dd>
          <dt>Email</dt>
          <dd>{patient.email ?? "—"}</dd>
          <dt>DNI</dt>
          <dd>{patient.documentId ?? "—"}</dd>
          <dt>Cuenta</dt>
          <dd>{patient.uid ? "Vinculada al portal" : "Sin cuenta"}</dd>
        </dl>
        <p className="label">Turnos</p>
        {sorted.length === 0 && <p className="muted small">Sin turnos registrados.</p>}
        <ul className="mini-list">
          {sorted.map((a) => (
            <li key={a.id}>
              <div>
                <strong className="cap">{formatClinicDate(a.start, { weekday: "short" })}</strong> <span className="mono">{formatClinicTime(a.start)}</span>
                <span className="muted small">{a.serviceName}</span>
              </div>
              <StatusPill status={a.status} />
            </li>
          ))}
        </ul>
        <div className="notice notice-info">
          <p>
            Historia clínica, odontograma, periodontograma, consentimientos y plan de tratamiento llegan en la <strong>Fase 3</strong>{" "}
            (núcleo clínico).
          </p>
        </div>
      </div>
    </aside>
  );
}

export function Patients() {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Patient | null>(null);
  const { data } = useLive<Patient>("patients", [orderBy("lastName"), limit(500)]);
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    const digits = t.replace(/\D/g, "");
    return (data ?? []).filter(
      (p) => !t || `${p.firstName} ${p.lastName} ${p.email ?? ""}`.toLowerCase().includes(t) || (digits.length > 2 && `${p.phone ?? ""}${p.documentId ?? ""}`.replace(/\D/g, "").includes(digits)),
    );
  }, [data, q]);

  return (
    <div className="page" data-panel={selected ? "open" : undefined}>
      <header className="toolbar">
        <div className="toolbar-title">
          <h1>Pacientes</h1>
          <p className="muted small">{data ? `${data.length} pacientes` : "Cargando…"}</p>
        </div>
        <div className="search search-inline">
          <Search size={16} aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, teléfono, DNI o email" aria-label="Buscar pacientes" />
        </div>
      </header>
      <div className="split">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Paciente</th>
                <th scope="col">Teléfono</th>
                <th scope="col">Email</th>
                <th scope="col">Portal</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} aria-selected={selected?.id === p.id} onClick={() => setSelected(p)}>
                  <td>
                    <button className="row-link" onClick={() => setSelected(p)}>
                      {p.lastName}, {p.firstName}
                    </button>
                  </td>
                  <td className="mono">{p.phone ?? "—"}</td>
                  <td>{p.email ?? "—"}</td>
                  <td>{p.uid ? "Sí" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && rows.length === 0 && <p className="empty small">Ningún paciente coincide con «{q}».</p>}
        </div>
        {selected && <PatientDetail patient={selected} onClose={() => setSelected(null)} />}
      </div>
    </div>
  );
}
